import React, { useState, useMemo } from 'react';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { CheckCircle2, XCircle, Minus, ChevronRight } from 'lucide-react';
import { vendorLogoUrl } from '../lib/logoUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ═══════════════════════════════════════════
// 1. SIMPLE GRID — 2-wide scrollable grid with integration lines
// ═══════════════════════════════════════════
export function SimpleGridView({ nodes, links, productLinks, onSelect, selectedId }) {
  const activeNodes = nodes.filter(n => n.used);

  // Build connection lookup
  const connMap = useMemo(() => {
    const m = {};
    [...links, ...productLinks].forEach(l => {
      const s = typeof l.source === 'object' ? l.source.id : l.source;
      const t = typeof l.target === 'object' ? l.target.id : l.target;
      if (!m[s]) m[s] = new Set();
      if (!m[t]) m[t] = new Set();
      m[s].add(t);
      m[t].add(s);
    });
    return m;
  }, [links, productLinks]);

  // Sort by connection count (most connected first)
  const sorted = [...activeNodes].sort((a, b) => ((connMap[b.id]?.size || 0) - (connMap[a.id]?.size || 0)));

  return (
    <ScrollArea className="h-full">
      <div className="p-3 pb-8" data-testid="simple-grid-view">
        <div className="grid grid-cols-2 gap-2" style={{ maxWidth: '100%' }}>
          {sorted.map(node => {
            const conns = connMap[node.id] || new Set();
            const isSelected = selectedId === node.id;
            const isConnected = selectedId && conns.has(selectedId);
            const isDimmed = selectedId && !isSelected && !isConnected;
            return (
              <button key={node.id} onClick={() => onSelect(node)}
                className={`rounded-xl border-2 p-2.5 text-left transition-all flex flex-col justify-between ${
                  isSelected ? 'ring-2 ring-offset-1 shadow-lg scale-[1.03]' :
                  isConnected ? 'ring-1 shadow-md' :
                  isDimmed ? 'opacity-20' : 'hover:shadow-md'
                }`}
                style={{ borderColor: isSelected || isConnected ? node.category_color : '#e2e8f0', '--tw-ring-color': node.category_color }}
                data-testid={`grid-${node.id}`}>
                <div>
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: node.category_color }} />
                    <span className="text-sm font-bold leading-tight truncate">{node.short || node.name}</span>
                  </div>
                  <span className="text-[10px] text-muted-foreground block truncate">{node.product_name || node.vendor_name || ''}</span>
                </div>
                {conns.size > 0 && (
                  <div className="flex items-center gap-1 flex-wrap mt-1.5">
                    {Array.from(conns).slice(0, 3).map(cid => {
                      const cn = nodes.find(n => n.id === cid);
                      return cn ? <span key={cid} className="text-[7px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 font-medium">{cn.short}{cn.product_name ? `: ${cn.product_name}` : ''}</span> : null;
                    })}
                    {conns.size > 3 && <span className="text-[7px] text-muted-foreground">+{conns.size - 3}</span>}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </ScrollArea>
  );
}

// ═══════════════════════════════════════════
// 2. TIMELINE VIEW — platforms by deployment order
// ═══════════════════════════════════════════
export function TimelineView({ nodes, onSelect }) {
  const activeNodes = nodes.filter(n => n.used);
  // Group by category and sort
  const byCategory = useMemo(() => {
    const groups = {};
    activeNodes.forEach(n => {
      const cat = n.category_name || 'Other';
      if (!groups[cat]) groups[cat] = { color: n.category_color, nodes: [] };
      groups[cat].nodes.push(n);
    });
    return Object.entries(groups).sort((a, b) => b[1].nodes.length - a[1].nodes.length);
  }, [activeNodes]);

  return (
    <ScrollArea className="h-full">
      <div className="p-3 pb-8" data-testid="timeline-view">
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-muted" />
          <div className="space-y-4 pl-10">
            {byCategory.map(([catName, cat]) => (
              <div key={catName}>
                <div className="flex items-center gap-2 mb-2 -ml-8">
                  <div className="w-4 h-4 rounded-full border-2 border-background z-10" style={{ backgroundColor: cat.color }} />
                  <span className="text-xs font-bold uppercase tracking-wider" style={{ color: cat.color }}>{catName}</span>
                  <span className="text-[9px] text-muted-foreground">{cat.nodes.length}</span>
                </div>
                <div className="space-y-1.5">
                  {cat.nodes.map(node => (
                    <button key={node.id} onClick={() => onSelect(node)}
                      className="w-full text-left p-3 rounded-xl border bg-card hover:shadow-md transition-all relative"
                      data-testid={`timeline-${node.id}`}>
                      <div className="absolute -left-8 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-muted-foreground/30" />
                      <span className="text-sm font-bold block">{node.product_name || node.vendor_name || node.short}</span>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-[8px]">{node.short}</Badge>
                        {node.vendor_company && <span className="text-[9px] text-muted-foreground">{node.vendor_company}</span>}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </ScrollArea>
  );
}

// ═══════════════════════════════════════════
// 3. SPEND TREEMAP — rectangles sized by cost
// ═══════════════════════════════════════════
export function SpendTreemap({ nodes, onSelect }) {
  const activeNodes = nodes.filter(n => n.used);
  // Group by category with equal weighting (no real cost data, use connection count as proxy)
  const items = useMemo(() => {
    return activeNodes.map(n => ({
      ...n,
      weight: Math.max(1, (n.solutions || []).length || 1),
    })).sort((a, b) => b.weight - a.weight);
  }, [activeNodes]);

  const total = items.reduce((s, i) => s + i.weight, 0) || 1;

  return (
    <ScrollArea className="h-full">
      <div className="p-3 pb-8" data-testid="treemap-view">
        <div className="flex flex-wrap gap-1" style={{ maxWidth: '100%' }}>
          {items.map(node => {
            const pct = (node.weight / total) * 100;
            const minW = Math.min(Math.max(pct * 2, 45), 100);
            return (
              <button key={node.id} onClick={() => onSelect(node)}
                className="rounded-lg p-2 text-left text-white hover:opacity-90 transition-opacity flex flex-col justify-end"
                style={{ backgroundColor: node.category_color || '#6366f1', width: `calc(${minW}% - 4px)`, minHeight: `${Math.max(pct * 1.2, 50)}px` }}
                data-testid={`treemap-${node.id}`}>
                <span className="text-[10px] font-bold leading-tight block truncate">{node.short}</span>
                <span className="text-[8px] opacity-70 truncate block">{node.product_name || ''}</span>
              </button>
            );
          })}
        </div>
      </div>
    </ScrollArea>
  );
}

// ═══════════════════════════════════════════
// 4. INTEGRATION MATRIX — X/Y grid of connections
// ═══════════════════════════════════════════
export function IntegrationMatrix({ nodes, links, productLinks, onSelect }) {
  const activeNodes = nodes.filter(n => n.used).slice(0, 15); // Limit for readability

  const connSet = useMemo(() => {
    const s = new Set();
    [...links, ...productLinks].forEach(l => {
      const src = typeof l.source === 'object' ? l.source.id : l.source;
      const tgt = typeof l.target === 'object' ? l.target.id : l.target;
      s.add(`${src}|${tgt}`);
      s.add(`${tgt}|${src}`);
    });
    return s;
  }, [links, productLinks]);

  const gapSet = useMemo(() => {
    const s = new Set();
    links.filter(l => l.integration_status === 'gap').forEach(l => {
      const src = typeof l.source === 'object' ? l.source.id : l.source;
      const tgt = typeof l.target === 'object' ? l.target.id : l.target;
      s.add(`${src}|${tgt}`);
      s.add(`${tgt}|${src}`);
    });
    return s;
  }, [links]);

  return (
    <div className="h-full overflow-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
      <div className="p-2 pb-8 overflow-auto" data-testid="matrix-view" style={{ WebkitOverflowScrolling: 'touch' }}>
        <table className="text-[8px] border-collapse">
          <thead>
            <tr>
              <th className="p-1 sticky left-0 bg-card z-10" />
              {activeNodes.map(n => (
                <th key={n.id} className="p-1 font-medium text-center" style={{ writingMode: 'vertical-lr', minWidth: '28px', color: n.category_color }}>
                  {n.short}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {activeNodes.map(row => (
              <tr key={row.id}>
                <td className="p-1 font-medium sticky left-0 bg-card z-10 truncate max-w-[60px]" style={{ color: row.category_color }}>{row.short}</td>
                {activeNodes.map(col => {
                  if (row.id === col.id) return <td key={col.id} className="p-1 text-center bg-muted/30">—</td>;
                  const key = `${row.id}|${col.id}`;
                  const connected = connSet.has(key);
                  const isGap = gapSet.has(key);
                  return (
                    <td key={col.id} className="p-1 text-center cursor-pointer hover:bg-muted/40" onClick={() => onSelect(row)}>
                      {isGap ? <XCircle className="h-3 w-3 text-red-400 mx-auto" /> :
                       connected ? <CheckCircle2 className="h-3 w-3 text-emerald-500 mx-auto" /> :
                       <Minus className="h-3 w-3 text-muted-foreground/30 mx-auto" />}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// 5. CATEGORY SWIMLANES — horizontal kanban lanes
// ═══════════════════════════════════════════
export function SwimlanesView({ nodes, links, productLinks, onSelect, selectedId }) {
  const activeNodes = nodes.filter(n => n.used);

  const connMap = useMemo(() => {
    const m = {};
    [...links, ...productLinks].forEach(l => {
      const s = typeof l.source === 'object' ? l.source.id : l.source;
      const t = typeof l.target === 'object' ? l.target.id : l.target;
      if (!m[s]) m[s] = new Set();
      if (!m[t]) m[t] = new Set();
      m[s].add(t);
      m[t].add(s);
    });
    return m;
  }, [links, productLinks]);

  const lanes = useMemo(() => {
    const groups = {};
    activeNodes.forEach(n => {
      const cat = n.category_name || 'Other';
      if (!groups[cat]) groups[cat] = { color: n.category_color, nodes: [] };
      groups[cat].nodes.push(n);
    });
    return Object.entries(groups).sort((a, b) => b[1].nodes.length - a[1].nodes.length);
  }, [activeNodes]);

  return (
    <ScrollArea className="h-full">
      <div className="p-3 pb-8 space-y-4" data-testid="swimlanes-view">
        {lanes.map(([catName, lane]) => (
          <div key={catName}>
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-white shadow-sm" style={{ backgroundColor: lane.color }}>
                {catName}
              </span>
              <span className="text-[9px] text-muted-foreground">{lane.nodes.length}</span>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-3 px-3" style={{ WebkitOverflowScrolling: 'touch', scrollSnapType: 'x mandatory' }}>
              {lane.nodes.map(node => {
                const conns = connMap[node.id] || new Set();
                const isSelected = selectedId === node.id;
                const isConnected = selectedId && conns.has(selectedId);
                return (
                  <button key={node.id} onClick={() => onSelect(node)}
                    className={`shrink-0 w-32 rounded-xl border-2 p-2.5 text-left transition-all ${
                      isSelected ? 'ring-2 shadow-lg scale-[1.03]' :
                      isConnected ? 'ring-1 shadow-md bg-blue-50/50' : 'hover:shadow-md'
                    }`}
                    style={{ borderColor: isSelected || isConnected ? lane.color : '#e2e8f0', '--tw-ring-color': lane.color }}
                    data-testid={`swim-${node.id}`}>
                    <div className="flex items-center gap-1.5">
                      {vendorLogoUrl(node) && (
                        <img src={vendorLogoUrl(node)} alt="" loading="lazy"
                          className="h-5 w-5 rounded border bg-white object-contain p-0.5 shrink-0"
                          onError={e => { e.target.style.display = 'none'; }} />
                      )}
                      <span className="text-xs font-bold block truncate">{node.product_name || node.short}</span>
                    </div>
                    <span className="text-[8px] text-muted-foreground block truncate mt-0.5">{node.vendor_company || ''}</span>
                    {conns.size > 0 && (
                      <div className="mt-1.5 flex gap-0.5 flex-wrap">
                        {Array.from(conns).slice(0, 2).map(cid => {
                          const cn = nodes.find(n => n.id === cid);
                          return cn ? <span key={cid} className="text-[6px] px-1 py-0.5 rounded bg-blue-50 text-blue-600">{cn.short}{cn.product_name ? `: ${cn.product_name}` : ''}</span> : null;
                        })}
                        {conns.size > 2 && <span className="text-[6px] text-muted-foreground">+{conns.size - 2}</span>}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </ScrollArea>
  );
}

// ═══════════════════════════════════════════
// 6. HEALTH PULSE — radar/gauge dashboard
// ═══════════════════════════════════════════
// Gauge ring component for Health Pulse
function GaugeRing({ value, size = 80, stroke = 6, color }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (value / 100) * circ;
  return (
    <svg width={size} height={size} className="block mx-auto">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="currentColor" strokeWidth={stroke} className="text-muted/30" />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`} className="transition-all duration-700" />
      <text x={size/2} y={size/2} textAnchor="middle" dominantBaseline="central"
        className="text-sm font-bold fill-foreground" style={{ fontFamily: 'Space Grotesk' }}>{value}%</text>
    </svg>
  );
}

export function HealthPulseView({ nodes, links, productLinks }) {
  const activeNodes = nodes.filter(n => n.used);
  const totalPlatforms = nodes.length;

  // Calculate metrics per category
  const metrics = useMemo(() => {
    const cats = {};
    nodes.forEach(n => {
      const cat = n.category_name || 'Other';
      if (!cats[cat]) cats[cat] = { color: n.category_color, total: 0, active: 0, connected: 0, gaps: 0 };
      cats[cat].total++;
      if (n.used) cats[cat].active++;
    });

    // Count connections and gaps per category
    links.forEach(l => {
      const s = typeof l.source === 'object' ? l.source : nodes.find(n => n.id === l.source);
      const t = typeof l.target === 'object' ? l.target : nodes.find(n => n.id === l.target);
      if (!s || !t) return;
      const sCat = s.category_name || 'Other';
      if (l.integration_status === 'gap') {
        if (cats[sCat]) cats[sCat].gaps++;
      } else {
        if (cats[sCat]) cats[sCat].connected++;
      }
    });

    return Object.entries(cats)
      .map(([name, data]) => ({
        name,
        ...data,
        coverage: data.total > 0 ? Math.round(data.active / data.total * 100) : 0,
        health: data.active > 0 ? Math.max(0, 100 - (data.gaps / Math.max(data.active, 1)) * 50) : 0,
      }))
      .sort((a, b) => b.active - a.active);
  }, [nodes, links]);

  const overallCoverage = totalPlatforms > 0 ? Math.round(activeNodes.length / totalPlatforms * 100) : 0;
  const totalConns = links.length;
  const totalGaps = links.filter(l => l.integration_status === 'gap').length;
  const integHealth = totalConns > 0 ? Math.round((totalConns - totalGaps) / totalConns * 100) : 100;

  return (
    <ScrollArea className="h-full">
      <div className="p-4 pb-8 space-y-6" data-testid="pulse-view">
        {/* Overall gauges */}
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center">
            <GaugeRing value={overallCoverage} color="#059669" />
            <span className="text-[9px] text-muted-foreground mt-1 block">Coverage</span>
          </div>
          <div className="text-center">
            <GaugeRing value={integHealth} color="#2563eb" />
            <span className="text-[9px] text-muted-foreground mt-1 block">Integration</span>
          </div>
          <div className="text-center">
            <GaugeRing value={Math.round((metrics.filter(m => m.health > 70).length / Math.max(metrics.length, 1)) * 100)} color="#7c3aed" />
            <span className="text-[9px] text-muted-foreground mt-1 block">Health</span>
          </div>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-4 gap-2">
          <div className="border rounded-lg p-2 text-center"><span className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>{activeNodes.length}</span><span className="text-[8px] text-muted-foreground block">Active</span></div>
          <div className="border rounded-lg p-2 text-center"><span className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>{totalConns}</span><span className="text-[8px] text-muted-foreground block">Links</span></div>
          <div className="border rounded-lg p-2 text-center"><span className="text-lg font-bold text-red-500" style={{ fontFamily: 'Space Grotesk' }}>{totalGaps}</span><span className="text-[8px] text-muted-foreground block">Gaps</span></div>
          <div className="border rounded-lg p-2 text-center"><span className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>{metrics.length}</span><span className="text-[8px] text-muted-foreground block">Categories</span></div>
        </div>

        {/* Category health bars */}
        <div className="space-y-2">
          <span className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Category Health</span>
          {metrics.map(m => (
            <div key={m.name} className="flex items-center gap-2 text-xs">
              <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: m.color }} />
              <span className="w-24 truncate font-medium">{m.name}</span>
              <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${m.coverage}%`, backgroundColor: m.color }} />
              </div>
              <span className="font-bold tabular-nums w-8 text-right">{m.coverage}%</span>
              <span className="text-[8px] text-muted-foreground w-10 text-right">{m.active}/{m.total}</span>
            </div>
          ))}
        </div>
      </div>
    </ScrollArea>
  );
}
